import { asClass, asValue, AwilixContainer } from 'awilix';
import { World } from 'miniplex';

import { BaseModule, MODULES_TOKEN } from './injectable/Module';
import { ZedEntity } from './Entity';

/**
 * The `Zed` class is the core class of the Zed SDK. It provides a container for modules and an API object that
 * aggregates the APIs of all registered modules.
 *
 * @remarks
 *
 * Usually, you will not need to instantiate this class directly.
 * Instead, you will use the {@link ZedBuilder} class to create
 *
 * @example
 *
 * ```ts
 * import { ZedBuilder } from 'core';
 * import { BuildingModule } from 'building';
 * import { InteriorModule } from 'interior';
 *
 * const zed = new ZedBuilder()
 *   .addModule(BuildingModule)
 *   .addModule(InteriorModule);
 *   .build();
 *
 * console.log(zed.api.building);
 * ```
 */
export class Zed<Api extends {}> {
  static readonly CONTAINER_TOKEN = Symbol.for('Zed.Container');
  static readonly WORLD_TOKEN = Symbol.for('Zed.World');

  public readonly api: Api;
  public readonly world: World<ZedEntity>;

  /**
   * for each module in the container, call its build method and merge its api to the api object
   */
  constructor(private readonly container: AwilixContainer) {
    container.register(Zed.CONTAINER_TOKEN, asValue(container));
    container.register(Zed.WORLD_TOKEN, asClass(World).setInjectionMode('CLASSIC').singleton());

    const modules = container.resolve(MODULES_TOKEN) as BaseModule[];
    const api = {};
    this.world = container.resolve(Zed.WORLD_TOKEN) as World;

    modules.forEach((module) => {
      const moduleApi = module.api;
      if (moduleApi) {
        this.deepMerge(api, moduleApi);
      }
    });

    this.api = api as Api;
  }

  dispose() {
    this.container.dispose();
  }

  private deepMerge(target: any, source: any) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (target[key] && typeof target[key] === 'object' && typeof source[key] === 'object') {
          if (!this.isClassInstance(target[key]) && !this.isClassInstance(source[key]))
            this.deepMerge(target[key], source[key]);
          else
            console.error(
              `순환참조 방지를 위해 class로 정의한 api는 다른 모듈의 api와 합칠 수 없습니다. 모듈의 [${key}] 관련 api를 확인하세요.`
            );
        } else {
          target[key] = source[key];
        }
      }
    }
  }

  private isClassInstance(obj: any): boolean {
    if (obj === null || obj === undefined) {
      return false;
    }

    // 객체의 생성자가 Object가 아니고, 프로토타입이 존재하면 클래스의 인스턴스로 간주
    // 이것은 클래스의 인스턴스가 일반적으로 생성자의 프로토타입을 상속받기 때문
    const proto = Object.getPrototypeOf(obj);
    return proto !== null && proto.constructor !== Object;
  }
}
